function createPresaleGuardedValidateSelectedItem(deps = {}) {
  const {
    validateSelectedItem,
    getPresaleState,
    saleStartUnix,
    getNowUnix = () => Math.floor(Date.now() / 1000),
    productTitle = '',
  } = deps;

  const invalid = [];
  if (typeof validateSelectedItem !== 'function') {
    invalid.push('validateSelectedItem: expected function');
  }
  if (typeof getPresaleState !== 'function') {
    invalid.push('getPresaleState: expected function');
  }
  if (typeof getNowUnix !== 'function') {
    invalid.push('getNowUnix: expected function');
  }
  if (invalid.length > 0) {
    throw new Error(`createPresaleGuardedValidateSelectedItem requires valid deps: ${invalid.join(', ')}`);
  }

  return function validatePresaleGuardedItem(options = {}) {
    const presaleState = getPresaleState({
      saleStartUnix,
      nowUnix: getNowUnix(),
    });
    if (presaleState && presaleState.isLocked) {
      return {
        ok: false,
        message: `販売開始前のため、まだカートに追加できません：${productTitle}`,
      };
    }
    return validateSelectedItem(options);
  };
}

function createBulkItemPresaleController({
  saleStartUnix,
  getNowMs = () => Date.now(),
  getPresaleCountdownState,
  formatPresaleRemaining,
  applyPresaleState,
  applyPresaleStateOptions = {},
  setIntervalFn = (callback, ms) => setInterval(callback, ms),
  clearIntervalFn = (id) => clearInterval(id),
  intervalMs = 1000,
}) {
  let intervalId = null;

  function stop() {
    if (intervalId === null) return;
    clearIntervalFn(intervalId);
    intervalId = null;
  }

  function sync() {
    const countdownState = getPresaleCountdownState({
      saleStartUnix,
      nowMs: getNowMs(),
    });
    const locked = Boolean(countdownState && countdownState.isLocked);
    const countdownText = locked
      ? formatPresaleRemaining(countdownState.diffMs)
      : '';

    applyPresaleState({
      ...applyPresaleStateOptions,
      locked,
      countdownText,
    });

    if (!locked) {
      stop();
    }
    return locked;
  }

  function start() {
    const locked = sync();
    if (locked && intervalId === null) {
      intervalId = setIntervalFn(sync, intervalMs);
    }
    return locked;
  }

  return {
    isRunning: () => intervalId !== null,
    start,
    stop,
    sync,
  };
}

function validateBulkOrderFactoryDeps(deps = {}) {
  const invalid = [];
  const functionDeps = [
    'parseVariantId',
    'isValidVariantId',
    'getSelectedQuantity',
    'getApplicantBirthdateIso',
    'validateBirthdateInputField',
    'setBirthdateError',
    'getApplicantAge',
    'isMinorAge',
    'getAttendeeRows',
    'validateAttendeeRow',
    'getAttendeeInputs',
    'isValidPhoneNumber',
    'hasMinor',
    'toNumber',
  ];
  const objectDeps = [
    'item',
  ];
  const booleanDeps = ['isTour'];

  functionDeps.forEach((name) => {
    if (typeof deps[name] !== 'function') {
      invalid.push(`${name}: expected function`);
    }
  });

  objectDeps.forEach((name) => {
    const value = deps[name];
    if (!value || (typeof value !== 'object' && typeof value !== 'function')) {
      invalid.push(`${name}: expected object`);
    }
  });

  booleanDeps.forEach((name) => {
    if (typeof deps[name] !== 'boolean') {
      invalid.push(`${name}: expected boolean`);
    }
  });

  if (invalid.length > 0) {
    throw new Error(`createBulkOrderValidateSelectedItem requires valid deps: ${invalid.join(', ')}`);
  }
}

function createBulkOrderValidateSelectedItem(deps = {}) {
  validateBulkOrderFactoryDeps(deps);

  const {
    parseVariantId,
    isValidVariantId,
    getSelectedQuantity,
    getApplicantBirthdateIso,
    validateBirthdateInputField,
    setBirthdateError,
    consentMain,
    getApplicantAge,
    isMinorAge,
    getAttendeeRows,
    validateAttendeeRow,
    getAttendeeInputs,
    isValidPhoneNumber,
    hasMinor,
    consentMinorWrap,
    consentMinor,
    item,
    isTour,
    birthdateYear,
    toNumber,
  } = deps;

  function normalizeAttendeeValidationResult(result) {
    if (result && typeof result === 'object') {
      const invalid = result.invalid && typeof result.invalid === 'object' ? result.invalid : {};
      return {
        ok: result.ok !== false,
        invalid: {
          name: invalid.name === true,
          age: invalid.age === true,
          tel: invalid.tel === true,
        },
        field: typeof result.field === 'string' ? result.field : '',
        message: typeof result.message === 'string' ? result.message : '',
      };
    }

    return {
      ok: result !== false,
      invalid: {},
      field: '',
      message: '',
    };
  }

  return function validateSelectedItem(options = {}) {
    const { showErrors = false } = options;
    const productTitle = item.dataset.productTitle || '';
    const variantId = parseVariantId(item.dataset.variantId);
    const quantity = getSelectedQuantity();

    if (!isValidVariantId(variantId)) {
      return { ok: false, message: `バリアントが取得できません：${productTitle}` };
    }
    if (!Number.isFinite(quantity) || quantity < 1) {
      return { ok: false, message: `数量が正しくありません：${productTitle}` };
    }
    if (!isTour) {
      return { ok: true, variantId, quantity, properties: {} };
    }

    if (!consentMain || !consentMinorWrap || !consentMinor || !birthdateYear) {
      return { ok: false, message: `ツアー入力フォームの初期化に失敗しました：${productTitle}` };
    }

    const birthdateIso = getApplicantBirthdateIso();
    if (!birthdateIso) {
      if (showErrors) {
        setBirthdateError('申込者（代表者）の生年月日を入力してください。');
        if (typeof birthdateYear.focus === 'function') birthdateYear.focus();
      }
      return { ok: false, message: `申込者（代表者）の生年月日を入力してください：${productTitle}` };
    }
    if (!validateBirthdateInputField()) {
      if (showErrors && typeof birthdateYear.focus === 'function') birthdateYear.focus();
      return { ok: false, message: `生年月日を正しく入力してください：${productTitle}` };
    }
    if (!consentMain.checked) {
      return { ok: false, message: `「ツアー参加規約同意」が未チェックです：${productTitle}` };
    }

    const properties = {
      '申込者_生年月日': birthdateIso,
      'ツアー参加規約同意': '同意済み',
    };
    const ages = [];
    const applicantAge = getApplicantAge();
    if (isMinorAge(applicantAge)) ages.push(applicantAge);

    const attendeeRows = getAttendeeRows();
    const attendeeCount = Math.max(0, quantity - 1);
    for (let i = 0; i < attendeeCount; i++) {
      const row = attendeeRows[i];
      if (!row) {
        return { ok: false, message: `同行者フォームの生成に失敗しました：${productTitle}` };
      }
      const rowValid = normalizeAttendeeValidationResult(validateAttendeeRow(row, { showErrors }));
      const { nameInput, ageInput, genderInput, telInput } = getAttendeeInputs(row);
      const name = nameInput?.value?.trim() || '';
      const age = toNumber(ageInput?.value?.trim() || '');
      const gender = genderInput?.value || '';
      const tel = telInput?.value?.trim() || '';

      if (!rowValid.ok) {
        const invalidField = rowValid.field || (
          rowValid.invalid.name ? 'name' : rowValid.invalid.age ? 'age' : rowValid.invalid.tel ? 'tel' : ''
        );
        if (showErrors) {
          if (invalidField === 'name' && nameInput && typeof nameInput.focus === 'function') {
            nameInput.focus();
          } else if (invalidField === 'age' && ageInput && typeof ageInput.focus === 'function') {
            ageInput.focus();
          } else if (invalidField === 'tel' && telInput && typeof telInput.focus === 'function') {
            telInput.focus();
          }
        }
        if (invalidField === 'name') {
          return { ok: false, message: `参加者${i + 1}の氏名を入力してください：${productTitle}` };
        }
        if (invalidField === 'age') {
          return { ok: false, message: `参加者${i + 1}の年齢を正しく入力してください：${productTitle}` };
        }
        if (invalidField === 'tel') {
          return { ok: false, message: `参加者${i + 1}の電話番号を正しく入力してください：${productTitle}` };
        }
        if (rowValid.message) {
          return { ok: false, message: `${rowValid.message}：${productTitle}` };
        }
        return { ok: false, message: `参加者${i + 1}の入力内容を確認してください：${productTitle}` };
      }

      ages.push(age);
      properties[`参加者${i + 1}_氏名`] = name;
      properties[`参加者${i + 1}_年齢`] = String(age);
      if (gender) properties[`参加者${i + 1}_性別`] = gender;
      if (tel) properties[`参加者${i + 1}_電話`] = tel;
    }

    const needsMinor = hasMinor(ages);
    if (needsMinor) {
      if (consentMinorWrap.hidden || !consentMinor.checked) {
        return { ok: false, message: `未成年参加の承諾が未チェックです：${productTitle}` };
      }
      properties['未成年参加_承諾'] = '承諾済み';
    }

    return { ok: true, variantId, quantity, properties };
  };
}

const bulkOrderExports = {
  createBulkItemPresaleController,
  createBulkOrderValidateSelectedItem,
  createPresaleGuardedValidateSelectedItem,
};

if (typeof module !== 'undefined' && module.exports) {
  module.exports = bulkOrderExports;
}

if (typeof window !== 'undefined') {
  window.MFBulkOrder = Object.assign({}, window.MFBulkOrder, bulkOrderExports);
}
